import { z } from "zod";
import { standardAuthFetch } from "@/data-access/core/authenticatedFetch";
import { apiEndpointBaseUrl } from "@/data-access/core/constants";

/**
 * Client for stored Template Studio conversations. Requests go through
 * naapi's report-engine proxy, which authenticates the user and forwards
 * their tenant and user to the layout-studio service.
 *
 * In local development, `VITE_TEMPLATE_STUDIO_CONVERSATIONS_URL` can point
 * at a locally running service instead. naapi is then not in the path, so
 * the client sends a test identity itself (`VITE_TEMPLATE_STUDIO_DEV_TENANT_ID`
 * and `VITE_TEMPLATE_STUDIO_DEV_USER_ID`, default `1`) and no user token.
 */

const localServiceUrl = import.meta.env.DEV
  ? import.meta.env.VITE_TEMPLATE_STUDIO_CONVERSATIONS_URL?.trim()
  : undefined;

const conversationsUrl =
  localServiceUrl ||
  `${apiEndpointBaseUrl}/report-engine/template-studio/conversations`;

const seedSchema = z.object({
  kind: z.literal("seed"),
  initialDraft: z.record(z.string(), z.unknown()).nullable(),
  previous: z.unknown().optional(),
  componentTheme: z.record(z.string(), z.unknown()).optional(),
});

/** Same body as a generate-layout response; Template Studio validates its fields when applying it. */
const turnResponseSchema = z.looseObject({ ok: z.boolean() });

const conversationSchema = z.object({
  conversationId: z.string(),
  scope: z.string(),
  revision: z.number().int().nonnegative(),
  seed: seedSchema,
  turns: z.array(
    z.looseObject({
      kind: z.enum(["message", "reset"]),
      requestId: z.string(),
      text: z.string(),
      revision: z.number().int().positive(),
      response: turnResponseSchema,
      /** The layout a reset returned to. */
      seed: seedSchema.optional(),
      images: z
        .array(z.object({ url: z.string(), name: z.string() }))
        .optional(),
      imagePlacement: z.enum(["top", "inside"]).optional(),
    }),
  ),
  pending: z
    .object({
      requestId: z.string(),
      text: z.string(),
      submittedAt: z.string().optional(),
    })
    .optional(),
});

export type TemplateStudioConversation = z.infer<typeof conversationSchema>;

/** `create`, `edit:<templateId>`, or `duplicate:<templateId>`. */
export type TemplateStudioConversationScope =
  | "create"
  | `edit:${string}`
  | `duplicate:${string}`;

export class TemplateStudioConversationError extends Error {
  readonly status: number;
  /** The service's error code, such as `REVISION_CONFLICT` or `TURN_PENDING`. */
  readonly code?: string;

  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = "TemplateStudioConversationError";
    this.status = status;
    this.code = code;
  }
}

const serviceErrorSchema = z.object({
  errors: z.array(z.object({ code: z.string(), message: z.string() })).min(1),
});
/** naapi's own proxy errors, such as an expired session or an unreachable service. */
const naapiErrorSchema = z.object({
  detail: z.string().optional(),
  error: z.string().optional(),
});

const readError = async (
  response: Response,
): Promise<TemplateStudioConversationError> => {
  const body: unknown = await response.json().catch(() => null);
  const service = serviceErrorSchema.safeParse(body);
  if (service.success) {
    const [first] = service.data.errors;
    return new TemplateStudioConversationError(
      response.status,
      first?.message ?? "",
      first?.code,
    );
  }
  const naapi = naapiErrorSchema.safeParse(body);
  const message = naapi.success
    ? naapi.data.detail || naapi.data.error
    : undefined;
  return new TemplateStudioConversationError(
    response.status,
    message || `Conversation request failed (${response.status}).`,
  );
};

const request = async <T>(
  path: string,
  schema: z.ZodType<T>,
  init: { method: "GET" | "POST"; body?: unknown; signal?: AbortSignal },
): Promise<T> => {
  const url = `${conversationsUrl}${path}`;
  const options: RequestInit = {
    method: init.method,
    signal: init.signal,
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  };
  const response = localServiceUrl
    ? await fetch(url, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          "X-NA-Tenant-Id":
            import.meta.env.VITE_TEMPLATE_STUDIO_DEV_TENANT_ID?.trim() || "1",
          "X-NA-User-Id":
            import.meta.env.VITE_TEMPLATE_STUDIO_DEV_USER_ID?.trim() || "1",
        },
      })
    : await standardAuthFetch(url, options);
  if (!response.ok) throw await readError(response);

  const parsed = z
    .object({ ok: z.literal(true) })
    .and(schema)
    .safeParse(await response.json().catch(() => null));
  if (!parsed.success) {
    throw new TemplateStudioConversationError(
      response.status,
      "The conversation service returned an unexpected response.",
    );
  }
  return parsed.data;
};

const withScope = (path: string, scope: TemplateStudioConversationScope) =>
  `${path}?scope=${encodeURIComponent(scope)}`;

const conversationResult = z.object({ conversation: conversationSchema });

export const createTemplateStudioConversation = async (body: {
  conversationId: string;
  scope: TemplateStudioConversationScope;
  initialDraft?: Record<string, unknown> | null;
  previous?: unknown;
  componentTheme?: Record<string, unknown>;
}): Promise<TemplateStudioConversation> =>
  (await request("", conversationResult, { method: "POST", body }))
    .conversation;

export const resumeTemplateStudioConversation = async (
  conversationId: string,
  scope: TemplateStudioConversationScope,
): Promise<TemplateStudioConversation> =>
  (
    await request(
      withScope(`/${encodeURIComponent(conversationId)}`, scope),
      conversationResult,
      { method: "GET" },
    )
  ).conversation;

export type TemplateStudioConversationCommand =
  | {
      kind: "message";
      requestId: string;
      expectedRevision: number;
      /** What the user typed, shown in history. */
      text: string;
      /** What the model receives, including Studio hints. */
      prompt: string;
      /** Stored images attached to the message. */
      images?: { url: string; name: string }[];
      /** Where the user said the attached image goes. */
      imagePlacement?: "top" | "inside";
      previewContext?: string;
      previous?: unknown;
      componentTheme?: Record<string, unknown>;
    }
  | {
      kind: "reset";
      requestId: string;
      expectedRevision: number;
      /** The draft to return to; `null` clears the canvas. */
      initialDraft: Record<string, unknown> | null;
      previous?: unknown;
      componentTheme?: Record<string, unknown>;
    };

/**
 * Runs and stores one turn. Retrying with the same `requestId` returns the
 * stored turn. Aborting stops waiting; the service still finishes the turn.
 */
export const sendTemplateStudioConversationTurn = async (
  conversationId: string,
  scope: TemplateStudioConversationScope,
  command: TemplateStudioConversationCommand,
  signal?: AbortSignal,
): Promise<TemplateStudioConversation> =>
  (
    await request(
      withScope(`/${encodeURIComponent(conversationId)}/turns`, scope),
      conversationResult,
      { method: "POST", body: command, signal },
    )
  ).conversation;
